import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Checkbox } from '@rudi/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import {
  useIdentityEntity,
  useLinkMention,
  useMergeIdentity,
  useNotSameIdentity,
  useSplitIdentity,
} from '../api/hooks';
import type { IdentityMention, IdentityPersonDetail } from '../api/types';
import {
  DataQualityNav,
  EntityLink,
  EntityPickerDialog,
  EntityReview,
  Fact,
  FlagButton,
  LinkEvidence,
  MentionPlace,
  ReasonField,
  identityErrorText,
  loadError,
} from './medical-data-quality';

export function InvestigatorRoutePage() {
  const { entityId } = useParams({ from: '/data-quality/investigators/$entityId' });
  return <InvestigatorDetailPage id={entityId} />;
}

type Dialogs = 'merge' | 'not-same' | 'split' | null;

/** One Investigator: every Mention with its raw name, email, Organisation and study, and the rule that linked it. */
export function InvestigatorDetailPage({ id }: { id: string }) {
  const entity = useIdentityEntity('person', id);
  const merge = useMergeIdentity();
  const notSame = useNotSameIdentity();
  const link = useLinkMention();
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [linking, setLinking] = useState<IdentityMention | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const data = entity.data;
  const detail = data && 'mentions' in data ? (data as IdentityPersonDetail) : null;
  const redirect = data?.redirect_to ?? null;
  const notFound = (entity.error as { response?: { status?: number } })?.response?.status === 404;
  const name = detail?.display_name ?? 'Investigator';
  const toggle = (mentionId: string, checked: boolean) =>
    setSelected(previous =>
      checked ? [...previous, mentionId] : previous.filter(item => item !== mentionId)
    );
  const canSplit = !!detail && selected.length > 0 && selected.length < detail.mentions.length;

  return (
    <div>
      <Link
        to="/data-quality/investigators"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Investigators
      </Link>
      <DataQualityNav />
      <QueryState isLoading={entity.isLoading} error={notFound ? null : loadError(entity.error)}>
        {notFound ? (
          <p role="alert">
            This Investigator was not found. It may have been removed; return to Investigators and
            search again.
          </p>
        ) : redirect ? (
          <div role="alert" className="rounded-3xl border bg-card p-4 text-sm">
            This Investigator was merged into another.{' '}
            <EntityLink kind="person" id={redirect}>
              Open the Investigator it was merged into
            </EntityLink>
            .
          </div>
        ) : detail ? (
          <>
            <PageHeader
              title={detail.display_name}
              description={`Investigator · ${detail.slug}`}
              actions={
                <div className="flex flex-wrap justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setDialog('merge')}>
                    Merge into…
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDialog('not-same')}>
                    Not the same as…
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canSplit}
                    title={
                      canSplit
                        ? undefined
                        : 'Select the Mentions of the other person, leaving at least one here'
                    }
                    onClick={() => setDialog('split')}
                  >
                    Split {selected.length || ''} selected
                  </Button>
                  <FlagButton level="person" id={detail.id} name={detail.display_name} />
                </div>
              }
            />
            <dl className="mb-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-3xl border bg-card p-4 text-sm">
              <Fact label="Organisations">
                {detail.tree.organisations.length
                  ? detail.tree.organisations.map((organisation, index) => (
                      <span key={organisation.id}>
                        {index > 0 && ', '}
                        <EntityLink kind="organisation" id={organisation.id}>
                          {organisation.display_name}
                        </EntityLink>
                      </span>
                    ))
                  : 'None'}
              </Fact>
              <Fact label="Studies">
                {detail.studies.map(study => study.registry_identifier).join(', ') || 'None'}
              </Fact>
              <Fact label="Former IDs">{detail.former_ids.join(', ') || 'None'}</Fact>
              <Fact label="Name">
                {detail.name_locked ? 'Set by a reviewer' : 'From the Mentions'}
              </Fact>
            </dl>
            <section aria-label="Mentions" className="mb-6 space-y-2">
              <h2 className="text-sm font-semibold">
                Mentions linked to this Investigator ({detail.mentions.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                Select the Mentions that belong to someone else, then split them into a new
                Investigator.
              </p>
              <div className="overflow-hidden rounded-3xl border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <span className="sr-only">Select</span>
                      </TableHead>
                      <TableHead>Name as written</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Study</TableHead>
                      <TableHead>Linked by</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.mentions.map(mention => (
                      <TableRow key={mention.id}>
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${mentionText(mention)}`}
                            checked={selected.includes(mention.id)}
                            onCheckedChange={checked => toggle(mention.id, checked === true)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {mention.person_name ?? 'Unnamed'}
                          {!mention.active && (
                            <Badge variant="outline" className="ml-2">
                              No longer in the registry
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {mention.email ?? <span className="text-muted-foreground">None</span>}
                        </TableCell>
                        <TableCell className="min-w-[14rem]">
                          <MentionPlace mention={mention} />
                          {mention.links.organisation.id && (
                            <div className="text-xs">
                              <EntityLink kind="organisation" id={mention.links.organisation.id}>
                                Open Organisation
                              </EntityLink>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {mention.registry_identifier}
                        </TableCell>
                        <TableCell>
                          <LinkEvidence link={mention.links.person} />
                          <Button
                            size="xs"
                            variant="outline"
                            className="mt-1"
                            aria-label={`Link ${mentionText(mention)} to another Investigator`}
                            onClick={() => setLinking(mention)}
                          >
                            Link to another…
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
            <EntityReview candidates={detail.candidates} findings={detail.findings} />
            <SplitDialog
              detail={detail}
              mentionIds={selected}
              open={dialog === 'split'}
              onClose={() => setDialog(null)}
              onDone={() => setSelected([])}
            />
          </>
        ) : null}
      </QueryState>
      <EntityPickerDialog
        open={dialog === 'merge'}
        onClose={() => setDialog(null)}
        level="person"
        title={`Merge ${name}`}
        description={`Choose the Investigator that ${name} really is. The Mentions move there and this ID redirects.`}
        confirmLabel="Merge"
        excludeIds={[id]}
        onConfirm={async (targetId, reason) => {
          if (!targetId) return;
          await merge.mutateAsync({ level: 'person', sourceId: id, targetId, reason });
          toast.success(`${name} merged`);
        }}
      />
      <EntityPickerDialog
        open={dialog === 'not-same'}
        onClose={() => setDialog(null)}
        level="person"
        title={`${name} is not the same as…`}
        description="Choose the other Investigator. Resolution never joins the two again."
        confirmLabel="Not the same"
        excludeIds={[id]}
        onConfirm={async (otherId, reason) => {
          if (!otherId) return;
          await notSame.mutateAsync({ level: 'person', leftId: id, rightId: otherId, reason });
          toast.success('Recorded as two people');
        }}
      />
      <EntityPickerDialog
        open={linking !== null}
        onClose={() => setLinking(null)}
        level="person"
        title={linking ? `Link ${mentionText(linking)} to another Investigator` : ''}
        description={`This Mention, and every Mention written the same way, moves to the Investigator you choose. The others stay with ${name}.`}
        confirmLabel="Link"
        excludeIds={[id]}
        onConfirm={async (personId, reason) => {
          if (!linking || !personId) return;
          await link.mutateAsync({
            mentionId: linking.id,
            level: 'person',
            entityId: personId,
            reason,
          });
          toast.success('Mention linked to the chosen Investigator');
        }}
      />
    </div>
  );
}

function mentionText(mention: IdentityMention): string {
  return `${mention.person_name ?? 'Mention'} in ${mention.registry_identifier}`;
}

function SplitDialog({
  detail,
  mentionIds,
  open,
  onClose,
  onDone,
}: {
  detail: IdentityPersonDetail;
  mentionIds: string[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const split = useSplitIdentity();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const moving = detail.mentions.filter(mention => mentionIds.includes(mention.id));
  const reset = () => {
    setReason('');
    setError(null);
    onClose();
  };
  const close = () => !split.isPending && reset();
  const confirm = async () => {
    setError(null);
    try {
      await split.mutateAsync({ level: 'person', id: detail.id, mentionIds, reason });
      toast.success(`Split ${mentionIds.length} Mentions into a new Investigator`);
      onDone();
      reset();
    } catch (failure) {
      setError(identityErrorText(failure, 'Could not split'));
    }
  };
  return (
    <Dialog open={open} onOpenChange={next => !next && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Split {detail.display_name}</DialogTitle>
          <DialogDescription>
            These Mentions become a new Investigator. The others stay with {detail.display_name}.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-1 text-sm">
          {moving.map(mention => (
            <li key={mention.id}>
              {mention.person_name ?? 'Unnamed'} · {mention.organisation_name ?? 'no organisation'}{' '}
              · {mention.registry_identifier}
            </li>
          ))}
        </ul>
        <ReasonField value={reason} onChange={setReason} />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={split.isPending}>
            Cancel
          </Button>
          <Button disabled={split.isPending} onClick={() => void confirm()}>
            Split
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
